import { useState } from "react";
import {
  TableRow,
  TableCell,
  Typography,
  Box,
  Tooltip,
  Link,
  InputBase,
  ToggleButtonGroup,
  ToggleButton,
  alpha,
  Chip,
  IconButton
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import StarIcon from "@mui/icons-material/Star";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HistoryIcon from "@mui/icons-material/History";
import { PastInteractionsModal } from "./PastInteractionsModal";
import { ClientResponseInteraction } from "../types/interactions";
import { formatDate } from "../../../../utils";

const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;

interface ClientResponseRowProps {
  interaction: ClientResponseInteraction;
  onUpdateResponse: (id: number, responseStatus: string | null, responseNote: string | null) => void;
}

export const ClientResponseRow = ({ interaction, onUpdateResponse }: ClientResponseRowProps) => {
  const theme = useTheme();
  const [noteValue, setNoteValue] = useState(interaction.responseNote ?? "");
  const [isPastInteractionsModalOpen, setIsPastInteractionsModalOpen] = useState(false);

  const handleStatusChange = (_: React.MouseEvent<HTMLElement>, newStatus: string | null) => {
    onUpdateResponse(interaction.id, newStatus, interaction.responseNote);
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNoteValue(value);
    onUpdateResponse(interaction.id, interaction.responseStatus, value);
  };

  const responseColor =
    interaction.responseStatus === "Extensively Responded" || interaction.responseStatus === "Responded"
      ? theme.palette.success.main
      : null;

  return (
    <TableRow
      sx={{
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        bgcolor: responseColor ? alpha(responseColor, 0.03) : "transparent",
        boxShadow: responseColor ? `inset 4px 0 0 ${responseColor}` : "none",
        "&:hover": {
          bgcolor: responseColor ? alpha(responseColor, 0.06) : alpha(theme.palette.primary.main, 0.02)
        },
        "&:last-child": { borderBottom: "none" },
        "& td": { py: 1.25 }
      }}
    >
      <TableCell>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip
              title={
                interaction.preferredName
                  ? `${interaction.contactName} (${interaction.preferredName})`
                  : interaction.contactName
              }
              arrow
            >
              <Link
                href={`/phonebook/contact/profile?id=${interaction.contactId}`}
                target="_blank"
                underline="hover"
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {interaction.contactName}
                  {interaction.preferredName && (
                    <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
                      {" "}
                      ({interaction.preferredName})
                    </Typography>
                  )}
                </Typography>
              </Link>
            </Tooltip>
            <Box display="flex" alignItems="center">
              {interaction.isClient && (
                <StarIcon sx={{ fontSize: "1rem", color: theme.palette.warning.main, flexShrink: 0 }} />
              )}
              {interaction.isDealOwner && (
                <AttachMoneyIcon sx={{ fontSize: "1rem", color: theme.palette.text.secondary, flexShrink: 0 }} />
              )}
            </Box>
            {interaction.pastInteractionsCount > 0 && (
              <Tooltip
                title={`${interaction.pastInteractionsCount} past interaction${
                  interaction.pastInteractionsCount > 1 ? "s" : ""
                }`}
                arrow
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPastInteractionsModalOpen(true);
                  }}
                  sx={{
                    p: 0.25,
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                  }}
                >
                  <HistoryIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {interaction.companyName && (
            <Tooltip title={interaction.companyName} arrow>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {interaction.companyName}
              </Typography>
            </Tooltip>
          )}
        </Box>
        {isPastInteractionsModalOpen && (
          <PastInteractionsModal
            open={isPastInteractionsModalOpen}
            onClose={() => setIsPastInteractionsModalOpen(false)}
            contactId={interaction.contactId}
            contactName={interaction.contactName}
            orderNumber={interaction.orderNumber}
            statusFilter={interaction.interactionCategory ?? ""}
          />
        )}
      </TableCell>

      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography variant="body2" color="text.secondary">
            {interaction.reasonName}
          </Typography>
          {interaction.orderNumber && (interaction.interactionCategory === "Received" || interaction.interactionCategory === "Closed") && (
            <Link href={`${momentumUrl}new-home/${interaction.orderNumber}`} target="_blank" underline="hover">
              <Typography variant="caption" fontWeight={600}>
                {interaction.orderNumber}
              </Typography>
            </Link>
          )}
        </Box>
      </TableCell>

      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {interaction.interactionDate ? formatDate(interaction.interactionDate) : null}
        </Typography>
      </TableCell>

      <TableCell sx={{ borderRight: `2px solid ${theme.palette.divider}` }}>
        {interaction.typeName && (
          <Chip
            label={interaction.typeName}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.72rem",
              borderRadius: "6px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.dark,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          />
        )}
      </TableCell>

      <TableCell sx={{ py: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={interaction.responseStatus}
            onChange={handleStatusChange}
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.7rem",
                px: 0.75,
                py: 0.25,
                borderRadius: "4px !important",
                border: `1px solid ${theme.palette.divider} !important`,
                mr: 0.5,
                "&.Mui-selected": {
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.dark,
                  borderColor: `${alpha(theme.palette.success.main, 0.4)} !important`
                }
              }
            }}
          >
            <ToggleButton value="Responded">Responded</ToggleButton>
            <ToggleButton value="Extensively Responded">Extensively Responded</ToggleButton>
          </ToggleButtonGroup>

          <InputBase
            value={noteValue}
            placeholder="Add note..."
            multiline
            minRows={1}
            maxRows={3}
            onChange={handleNoteChange}
            sx={{
              flex: 1,
              fontSize: "0.75rem",
              px: 0.75,
              py: 0.25,
              borderRadius: "4px",
              border: `1px solid ${alpha(theme.palette.divider, 1)}`,
              bgcolor: alpha(theme.palette.grey[50], 0.5),
              "&:hover": {
                borderColor: alpha(theme.palette.primary.main, 0.3),
                bgcolor: theme.palette.background.paper
              },
              "&:focus-within": {
                borderColor: theme.palette.primary.main,
                bgcolor: theme.palette.background.paper,
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
              }
            }}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
};
